import type { Cart, Money, Product, ProductVariant } from './types';

/** "64,99 €" / "€64.99" — amount and currency always come from Shopify. */
export function formatMoney(money: Money, locale: string) {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: money.currencyCode }).format(
    Number(money.amount)
  );
}

/** Compare-at price only when it is really higher than the current price. */
export function hasDiscount(variant: ProductVariant) {
  return (
    !!variant.compareAtPrice &&
    Number(variant.compareAtPrice.amount) > Number(variant.price.amount)
  );
}

/** First variant that can be bought (or the first one, to show its price as sold out). */
export function defaultVariant(product: Product) {
  return product.variants.find((v) => v.availableForSale) ?? product.variants[0] ?? null;
}

/** Shopify's placeholder name for products without options. */
export const isDefaultTitle = (title: string) => title === 'Default Title';

/** Discounts Shopify applied to the cart, grouped by name (title or code), amounts summed. */
export function cartDiscounts(cart: Cart): { label: string | null; amount: Money }[] {
  const groups = new Map<string, { label: string | null; amount: Money }>();
  for (const line of cart.lines) {
    for (const allocation of line.discountAllocations ?? []) {
      const label = allocation.title ?? allocation.code ?? null;
      const key = label ?? '';
      const current = groups.get(key);
      const added = Number(allocation.discountedAmount.amount);
      if (!Number.isFinite(added) || added <= 0) continue;
      if (current) current.amount = { ...current.amount, amount: String(Number(current.amount.amount) + added) };
      else groups.set(key, { label, amount: { ...allocation.discountedAmount, amount: String(added) } });
    }
  }
  return [...groups.values()];
}

/** True when Shopify discounted this line (the total is lower than the subtotal). */
export const lineIsDiscounted = (line: Cart['lines'][number]) =>
  Number(line.cost.totalAmount.amount) < Number(line.cost.subtotalAmount.amount);
